import { useState } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import type { QuizQuestion } from '../types'

type Props = {
  questions: QuizQuestion[]
  onFinish: (answeredQuestions: QuizQuestion[]) => void
  onCancel: () => void
}

export function QuizExecutor({ questions, onFinish, onCancel }: Props) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(
    () => questions.map(() => null)
  )

  const question = questions[currentIndex]
  const total = questions.length
  const progress = ((currentIndex + 1) / total) * 100
  const answeredCount = answers.filter(a => a !== null).length

  const selectAnswer = (optionIndex: number) => {
    setAnswers(prev => {
      const next = [...prev]
      next[currentIndex] = optionIndex
      return next
    })
  }

  const goNext = () => {
    if (currentIndex < total - 1) {
      setCurrentIndex(i => i + 1)
    }
  }

  const goPrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1)
    }
  }

  const handleFinish = () => {
    const answered = questions.map((q, i) => ({
      ...q,
      user_answer: answers[i],
      is_correct: answers[i] !== null ? answers[i] === q.correct_option : null,
    }))
    onFinish(answered)
  }

  const isLast = currentIndex === total - 1

  const categoryColor: Record<string, string> = {
    technical: 'bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300',
    conceptual: 'bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300',
    behavioral: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm text-app-muted">
          <span>Question {currentIndex + 1} of {total}</span>
          <span>{answeredCount} / {total} answered</span>
        </div>
        <div className="h-2 rounded-full bg-app-subtle overflow-hidden">
          <div
            className="h-full rounded-full bg-sky-500 transition-all duration-300"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question card */}
      <div className="card p-6 space-y-5">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${categoryColor[question.category] || 'bg-app-subtle text-app-ink'}`}>
            {question.category}
          </span>
        </div>

        <h2 className="text-lg font-semibold text-app-ink leading-relaxed font-display">
          {question.text}
        </h2>

        <div className="space-y-3">
          {question.options.map((option, idx) => {
            const isSelected = answers[currentIndex] === idx
            return (
              <button
                key={idx}
                onClick={() => selectAnswer(idx)}
                className={`w-full text-left rounded-xl border-2 px-4 py-3 text-sm transition-all ${
                  isSelected
                    ? 'border-app-accent bg-app-highlight text-app-accent ring-1 ring-app-accent/30'
                    : 'border-app-line bg-app-surface text-app-ink hover:border-app-line hover:bg-app-subtle'
                }`}
              >
                {option}
              </button>
            )
          })}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={onCancel}
          className="text-sm text-app-muted hover:text-app-ink transition-colors"
        >
          Cancel Quiz
        </button>

        <div className="flex items-center gap-3">
          <button
            onClick={goPrev}
            disabled={currentIndex === 0}
            className="flex items-center gap-1.5 rounded-lg border border-app-line px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Previous
          </button>

          {isLast ? (
            <button
              onClick={handleFinish}
              className="flex items-center gap-1.5 rounded-lg bg-app-primary px-5 py-2 text-sm font-semibold text-white hover:bg-app-primary-hover transition-colors"
            >
              Finish Quiz
            </button>
          ) : (
            <button
              onClick={goNext}
              className="flex items-center gap-1.5 rounded-lg bg-app-primary px-5 py-2 text-sm font-medium text-white hover:bg-app-primary-hover transition-colors"
            >
              Next
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {/* Question dots navigation */}
      <div className="flex flex-wrap justify-center gap-1.5 pt-2">
        {questions.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`h-2.5 w-2.5 rounded-full transition-all ${
              idx === currentIndex
                ? 'bg-app-primary scale-125'
                : answers[idx] !== null
                  ? 'bg-app-highlight'
                  : 'bg-app-line'
            }`}
            title={`Question ${idx + 1}`}
          />
        ))}
      </div>
    </div>
  )
}
